import {FC, useMemo} from 'react'
import {buildContentStoryEvents} from '../buildContentStory'
import {PrintStoryDoc, PrintStoryEvent} from '../types'
import {formatDuration, formatElapsedSince, formatRelative, formatTimestamp} from '../time'
import {SeverityBadge} from './SeverityBadge'
import {severityBarColor} from '../severity'

const resolveCodeLabel = (event: PrintStoryEvent, codes: PrintStoryDoc[]): string | undefined => {
  const raw = event.raw as PrintStoryDoc | undefined
  const eventCodeId = event.codeId ? String(event.codeId) : ''
  const match = codes.find((code) => {
    const id = String(code.codeId || code.id || '')
    const printable = code.code ? String(code.code) : ''
    return (
      (eventCodeId && (id === eventCodeId || printable === eventCodeId)) ||
      (raw?.codeId && id === String(raw.codeId))
    )
  })

  return (
    (match?.code ? String(match.code) : undefined) ||
    (raw?.code ? String(raw.code) : undefined) ||
    (match?.codeId ? String(match.codeId) : undefined) ||
    (eventCodeId || undefined)
  )
}

const formatRouteContext = (event: PrintStoryEvent, codes: PrintStoryDoc[]): string | undefined => {
  const parts: string[] = []
  const codeLabel = resolveCodeLabel(event, codes)
  if (codeLabel && event.kind !== 'code_assigned') {
    parts.push(`Code ${codeLabel}`)
  }
  if (event.deviceId) {
    parts.push(`Printer ${event.deviceId}`)
  }
  return parts.length > 0 ? parts.join(' · ') : undefined
}

const isDeviceOnlyDetail = (event: PrintStoryEvent): boolean => {
  if (!event.detail || !event.deviceId) {
    return false
  }
  return event.detail.trim() === `Device ${event.deviceId}`
}

type Props = {
  content?: PrintStoryDoc | null
  codes?: PrintStoryDoc[]
  renderJobs?: PrintStoryDoc[]
  printJobs?: PrintStoryDoc[]
}

/** Compact chronological lifecycle for the Basic tab */
export const ContentLifecycleStrip: FC<Props> = ({
  content,
  codes = [],
  renderJobs = [],
  printJobs = [],
}) => {
  const events = useMemo(
    () =>
      buildContentStoryEvents({
        content,
        codes,
        renderJobs,
        printJobs,
        includeSecondaryLogs: false,
      }).filter((event) =>
        ['content', 'code', 'mail', 'render', 'prefetch', 'print', 'callback'].includes(event.lane)
      ),
    [content, codes, renderJobs, printJobs]
  )

  const originTs = events[0]?.ts

  if (events.length === 0 || originTs === undefined) {
    return (
      <div className='card card-flush border border-gray-200 mb-7'>
        <div className='card-body py-5'>
          <div className='fw-bold text-gray-800 mb-1'>Lifecycle</div>
          <div className='text-muted fs-7'>No lifecycle timestamps available yet.</div>
        </div>
      </div>
    )
  }

  return (
    <div className='card card-flush border border-gray-200 mb-7'>
      <div className='card-header border-0 pt-5 pb-0'>
        <div className='card-title flex-column align-items-start'>
          <span className='fw-bold text-gray-800'>Lifecycle</span>
          <span className='text-muted fs-7 fw-semibold mt-1'>
            Timers start at the first step (T+0ms)
          </span>
        </div>
      </div>
      <div className='card-body pt-4'>
        <div className='position-relative'>
          {events.map((event, index) => {
            const isLast = index === events.length - 1
            const elapsed = formatElapsedSince(event.ts, originTs)
            const stepGap =
              index === 0 ? null : `+${formatDuration(Math.max(0, event.ts - events[index - 1].ts))}`
            const routeContext = formatRouteContext(event, codes)
            const showDetail = Boolean(event.detail) && !isDeviceOnlyDetail(event)

            return (
              <div
                key={`${event.kind}-${event.ts}-${index}`}
                className='d-flex align-items-stretch gap-4'
              >
                <div
                  className='d-flex flex-column align-items-center'
                  style={{width: 56, flexShrink: 0}}
                >
                  <span className='badge badge-light-primary fw-bold fs-8 mb-1' title='Since first step'>
                    {elapsed}
                  </span>
                  <span
                    className='rounded-circle border border-white'
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: severityBarColor(event.severity),
                      boxShadow: '0 0 0 3px rgba(239,242,245,1)',
                      zIndex: 1,
                    }}
                  />
                  {!isLast && (
                    <span
                      className='flex-grow-1'
                      style={{
                        width: 2,
                        minHeight: 28,
                        backgroundColor: '#e4e6ef',
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>

                <div className={`flex-grow-1 ${isLast ? 'pb-0' : 'pb-5'}`}>
                  <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                      <span className='badge badge-light fw-bold text-gray-700'>
                        Step {index + 1}
                      </span>
                      <SeverityBadge severity={event.severity}>{event.lane}</SeverityBadge>
                      <span className='fw-bold text-gray-800'>{event.title}</span>
                    </div>
                    <div className='text-muted fs-8 text-nowrap' title={formatTimestamp(event.ts)}>
                      {formatRelative(event.ts)}
                    </div>
                  </div>
                  {showDetail && <div className='text-muted fs-7 mt-1'>{event.detail}</div>}
                  {routeContext && <div className='text-muted fs-8 mt-1'>{routeContext}</div>}
                  <div className='d-flex flex-wrap gap-3 mt-1 text-muted fs-8'>
                    <span>{formatTimestamp(event.ts)}</span>
                    {stepGap && <span className='text-gray-600 fw-semibold'>{stepGap} from previous</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
